import React from 'react';

interface PasswordCriteriaHintProps {
  /** Current password value. */
  password: string;
  /** Whether the password input is currently focused. */
  focused: boolean;
}

/**
 * Live password requirement hint used by the login and register forms.
 *
 * Behaviour:
 * - Hidden by default (empty value, never focused).
 * - Shown while the field is focused, or after leaving it when something was
 *   typed but the criteria are not yet fully met.
 * - Each requirement is green when met, red when unmet while the field is
 *   active (focused or has input), and neutral otherwise.
 *
 * The checks mirror the password regex used in `passwordSchema`
 * (see password-field.tsx).
 */
const PasswordCriteriaHint: React.FC<PasswordCriteriaHintProps> = ({
  password,
  focused,
}) => {
  const criteria = {
    eight: password.length >= 8,
    capital: /[A-Z]/.test(password),
    small: /[a-z]/.test(password),
    digit: /\d/.test(password),
    symbol: /[^a-zA-Z0-9]/.test(password),
  };

  const allCriteriaMet = Object.values(criteria).every(Boolean);
  const showCriteria = focused || (password.length > 0 && !allCriteriaMet);

  if (!showCriteria) {
    return null;
  }

  // Neutral by default (no input and not focused), green when met, otherwise
  // highlight unmet criteria in red.
  const criteriaClass = (met: boolean) => {
    if (met) {
      return 'text-(--vel-color-semantic-success-high)!';
    }
    return password.length === 0 && !focused
      ? 'text-(--vel-color-secondary-400)!'
      : 'text-(--vel-color-semantic-error-high)!';
  };

  return (
    <p className='mt-1! text-xs font-normal'>
      <span className={criteriaClass(criteria.eight)}>
        Mindestens 8 Zeichen lang
      </span>
      ,{' '}
      <span className={criteriaClass(criteria.capital)}>
        einen Großbuchstaben
      </span>
      ,{' '}
      <span className={criteriaClass(criteria.small)}>
        einen Kleinbuchstaben
      </span>
      , <span className={criteriaClass(criteria.digit)}>eine Zahl</span>,{' '}
      <span className={criteriaClass(criteria.symbol)}>
        ein Sonderzeichen (z.B. !, @, #, $)
      </span>
    </p>
  );
};

export default PasswordCriteriaHint;
